import axios, { AxiosInstance } from 'axios';
import { apiConfig } from '@/config/apiConfig';
import { getVoiceFallbackClient } from './_shared';
import type { Offer } from './offers';
import { parseTranscriptStringBlob } from './conversations';
import type { ConversationLine } from './conversations';

// ── Knowledge base (/knowledge-base) ────────────────────────────────

export type KbCategory = { id: string; label: string; icon: string; count: number };
export type KbConnection = { id: string; label: string; online: boolean };
export type KbDocument = {
  id: string;
  name: string;
  icon: string;
  meta: string;
  status: string;
  chunks: number;
  agents: string[];
  category: string;
  type: string;
  locked?: boolean;
  /** Null for docs not tied to one specific campaign (e.g. agent-wide uploads) — kept
   * visible under every campaign filter rather than hidden as a false "wrong campaign". */
  campaign_id?: string | null;
};

export type KnowledgeBasePayload = {
  categories: KbCategory[];
  connections: KbConnection[];
  documents: KbDocument[];
};

const INITIAL_KB_DOCUMENTS: KbDocument[] = [
  { id: '1', name: 'Spring_HVAC_Offer.pdf', icon: 'solar:file-text-bold', meta: '1.2 MB · 8 pages · Campaign Offer', status: 'processed', chunks: 24, agents: ['HomeServ Agent'], category: 'offer', type: 'pdf' },
  { id: '2', name: 'Pricing_Tiers.csv', icon: 'solar:chart-square-bold', meta: '48 KB · 3 sheets · Pricing', status: 'processed', chunks: 12, agents: ['HomeServ Agent', 'PlumbRight'], category: 'pricing', type: 'csv' },
  { id: '3', name: 'FAQ_HomeServices.docx', icon: 'solar:document-text-bold', meta: '220 KB · 15 pages · FAQs', status: 'processed', chunks: 38, agents: ['All agents'], category: 'faq', type: 'pdf' },
  { id: '4', name: 'Service_Areas.csv', icon: 'solar:map-point-bold', meta: '32 KB · Eligibility', status: 'processed', chunks: 8, agents: [], category: 'eligibility', type: 'csv' },
  { id: '5', name: 'TCPA_Disclosure.txt', icon: 'solar:shield-check-bold', meta: '4 KB · Compliance', status: 'processed', chunks: 3, agents: [], category: 'compliance', type: 'pdf', locked: true },
  { id: '6', name: 'boostt.ai/hvac-offer', icon: 'solar:globus-bold', meta: 'URL crawl · Campaign Offer', status: 'pending', chunks: 0, agents: [], category: 'offer', type: 'url' },
  { id: '7', name: 'Emergency_Plumbing_FAQ.pdf', icon: 'solar:file-text-bold', meta: '86 KB · 6 pages · FAQs', status: 'processed', chunks: 16, agents: ['PlumbRight'], category: 'faq', type: 'pdf' },
  { id: '8', name: 'Warranty_Terms.pdf', icon: 'solar:file-text-bold', meta: '140 KB · FAQs', status: 'processed', chunks: 10, agents: ['All agents'], category: 'faq', type: 'pdf' },
  { id: '9', name: 'DNC_Script_Template.txt', icon: 'solar:shield-check-bold', meta: '2 KB · Compliance', status: 'processed', chunks: 2, agents: [], category: 'compliance', type: 'pdf', locked: true },
  { id: '10', name: 'homeserv.com/services', icon: 'solar:globus-bold', meta: 'URL crawl · FAQs', status: 'processed', chunks: 14, agents: ['HomeServ Agent'], category: 'faq', type: 'url' },
  { id: '11', name: 'Plan_Comparison_2026.csv', icon: 'solar:chart-square-bold', meta: '28 KB · Pricing', status: 'processed', chunks: 6, agents: ['All agents'], category: 'pricing', type: 'csv' },
  { id: '12', name: 'Spring_Promo_Landing.pdf', icon: 'solar:file-text-bold', meta: '540 KB · Campaign Offer', status: 'processed', chunks: 18, agents: ['HomeServ Agent'], category: 'offer', type: 'pdf' },
];

let kbDocumentsStore: KbDocument[] = INITIAL_KB_DOCUMENTS.map((d) => ({ ...d, agents: [...d.agents] }));

const INITIAL_KB_CONNECTIONS: KbConnection[] = [
  { id: 'hubspot', label: 'CRM · HubSpot', online: true },
  { id: 'calendly', label: 'Calendar · Calendly', online: true },
];

let kbConnectionsStore: KbConnection[] = [...INITIAL_KB_CONNECTIONS];

function buildKbCategories(): KbCategory[] {
  const docs = kbDocumentsStore;
  return buildKbCategoriesFromList(docs);
}

export function buildKbCategoriesFromList(docs: KbDocument[]): KbCategory[] {
  const n = (cat: string) => docs.filter((d) => d.category === cat).length;
  return [
    { id: 'all', label: 'All sources', icon: 'solar:folder-2-bold', count: docs.length },
    { id: 'offer', label: 'Campaign Offer', icon: 'solar:target-bold', count: n('offer') },
    { id: 'pricing', label: 'Pricing & Plans', icon: 'solar:dollar-minimalistic-bold', count: n('pricing') },
    { id: 'faq', label: 'FAQs', icon: 'solar:question-circle-bold', count: n('faq') },
    { id: 'eligibility', label: 'Eligibility Rules', icon: 'solar:clipboard-list-bold', count: n('eligibility') },
    { id: 'compliance', label: 'Compliance Scripts', icon: 'solar:shield-check-bold', count: n('compliance') },
  ];
}

function inferKbFileTypeFromName(name: string): KbDocument['type'] {
  const lower = name.toLowerCase();
  if (lower.startsWith('http://') || lower.startsWith('https://')) return 'url';
  if (lower.endsWith('.csv')) return 'csv';
  if (lower.endsWith('.txt') || lower.endsWith('.text')) return 'txt';
  if (lower.endsWith('.md') || lower.endsWith('.markdown')) return 'md';
  return 'pdf';
}

/** Normalize `/api/knowledge/*` document records for the knowledge-base UI. */
export function mapApiKnowledgeDocument(d: any): KbDocument {
  const name = String(d.source_url ?? d.title ?? d.filename ?? d.name ?? d.original_filename ?? d.url ?? 'Document');
  const rawType = String(d.source_type ?? d.mime_type ?? d.type ?? '').toLowerCase();
  let fileType = inferKbFileTypeFromName(name);
  if (rawType.includes('url') || rawType.includes('text/html') || d.source_url) fileType = 'url';
  else if (rawType.includes('csv') || rawType.includes('sheet')) fileType = 'csv';
  else if (rawType.includes('markdown')) fileType = 'md';
  else if (rawType.includes('text/plain')) fileType = 'txt';
  const status = String(d.status ?? d.state ?? 'unknown');
  const chunks = Number(d.chunk_count ?? d.chunks ?? d.num_chunks ?? 0) || 0;
  // Normalize full category labels (from the DB `categories text[]` column) to short IDs
  const CATEGORY_LABEL_MAP: Record<string, string> = {
    'campaign offer': 'offer',
    'pricing & plans': 'pricing',
    'pricing and plans': 'pricing',
    'pricing': 'pricing',
    'faqs': 'faq',
    'faq': 'faq',
    'eligibility rules': 'eligibility',
    'eligibility': 'eligibility',
    'compliance scripts': 'compliance',
    'compliance': 'compliance',
    'offer': 'offer',
  };
  const rawCategory = Array.isArray(d.categories) && d.categories.length > 0
    ? String(d.categories[0])
    : String(d.category ?? 'faq');
  let category = CATEGORY_LABEL_MAP[rawCategory.toLowerCase()] ?? 'faq';
  const icon =
    fileType === 'url' ? 'solar:globus-bold' :
    fileType === 'csv' ? 'solar:chart-square-bold' :
    fileType === 'md' || fileType === 'txt' ? 'solar:document-text-bold' :
    'solar:file-text-bold';
  const bytes = d.size_bytes ?? d.file_size;
  const metaParts: string[] = [];
  if (bytes != null && !Number.isNaN(Number(bytes))) metaParts.push(`${Math.max(1, Math.round(Number(bytes) / 1024))} KB`);
  if (d.chars_extracted != null) metaParts.push(`${Number(d.chars_extracted).toLocaleString()} chars`);
  if (d.page_count != null) metaParts.push(`${d.page_count} pages`);
  metaParts.push(status);
  // Prefer the first NON-EMPTY candidate — an empty array from a higher-priority
  // field (e.g. `agent_names: []`) must not hide real associations sitting in a
  // lower-priority field (e.g. `agent_ids: [...]`).
  const agentsCandidate = [d.agent_names, d.agents, d.agent_ids, d.agentIds, d.assigned_agents]
    .find((arr) => Array.isArray(arr) && arr.length > 0);
  const agents = (agentsCandidate ?? []).map(String);
  return {
    id: String(d.id ?? d.doc_id ?? d.document_id ?? name),
    name,
    icon,
    meta: metaParts.join(' · '),
    status,
    chunks,
    agents,
    category,
    type: fileType,
    locked: Boolean(d.locked ?? d.is_locked),
    campaign_id: d.campaign_id ? String(d.campaign_id) : null,
  };
}

async function fetchKbConnectionsMerged(client: AxiosInstance): Promise<KbConnection[]> {
  try {
    const res = await client.get<any[]>(`${apiConfig.voiceEndpoints.integrations}/connected`);
    const rows = (res.data || []).map((c: any) => ({
      id: String(c.id),
      label: String(c.name ? `${c.integration_id ?? 'integration'} · ${c.name}` : c.integration_id ?? 'Connected'),
      online: String(c.status ?? 'active').toLowerCase() !== 'error',
    }));
    if (rows.length) return rows;
  } catch {
    /* use defaults */
  }
  return kbConnectionsStore.map((c) => ({ ...c }));
}

function cloneKbDocument(d: KbDocument): KbDocument {
  return { ...d, agents: [...d.agents] };
}

export async function fetchKnowledgeBase(agentId?: string, axiosInstance?: AxiosInstance): Promise<KnowledgeBasePayload> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.knowledge}/by-workspace`;
  const [docRes, connections] = await Promise.all([
    client.get<unknown>(url, { params: agentId ? { agent_id: agentId } : {} }),
    fetchKbConnectionsMerged(client)
  ]);
  const payload = docRes.data as any;
  const rawList = Array.isArray(payload) ? payload : payload?.documents ?? payload?.items ?? [];
  const documents = (rawList as any[]).map(mapApiKnowledgeDocument);
  return {
    categories: buildKbCategoriesFromList(documents),
    connections,
    documents,
  };
}

export type CreateKnowledgeDocumentInput =
  | { type: 'file'; file: File; agent_id?: string; campaign_id?: string; auto_approve?: boolean; categories?: string[]; category?: string }
  | { type: 'url'; url: string; agent_id?: string; campaign_id?: string; categories?: string[]; category?: string };

export async function createKnowledgeDocument(input: CreateKnowledgeDocumentInput, axiosInstance?: AxiosInstance): Promise<KbDocument> {
  const client = axiosInstance || getVoiceFallbackClient();

  if (input.type === 'url') {
    const response = await client.post<any>(`${apiConfig.voiceEndpoints.knowledge}/crawl-url`, {
      url: input.url,
      agent_id: input.agent_id,
      campaign_id: input.campaign_id,
      categories: input.categories || [input.category || 'FAQ'],
    });
    return mapApiKnowledgeDocument(response.data);
  } else {
    const formData = new FormData();
    formData.append('file', input.file);
    if (input.agent_id) formData.append('agent_id', input.agent_id);
    if (input.campaign_id) formData.append('campaign_id', input.campaign_id);
    formData.append('auto_approve', String(input.auto_approve ?? true));
    if (input.categories?.length) {
      formData.append('categories', JSON.stringify(input.categories));
    } else if (input.category) {
      formData.append('categories', JSON.stringify([input.category]));
    }
    const response = await client.post<any>(`${apiConfig.voiceEndpoints.knowledge}/upload`, formData);
    return mapApiKnowledgeDocument(response.data);
  }
}

/**
 * Fetch specific conversation transcript
 */
/** Normalize `/api/analytics/conversation/{session_id}` payloads into transcript lines. */
export function normalizeConversationTranscript(data: unknown): ConversationLine[] {
  if (Array.isArray(data)) {
    return (data as any[])
      .map((line: any) => {
        const roleRaw = String(line.role ?? line.speaker ?? 'agent').toLowerCase();
        const role: 'agent' | 'user' =
          roleRaw === 'user' || roleRaw === 'caller' || roleRaw === 'human' || roleRaw === 'customer' ? 'user' : 'agent';
        const text = String(line.content ?? line.text ?? line.message ?? '');
        return { role, text };
      })
      .filter((l) => l.text && l.text.toLowerCase() !== 'none');
  }
  const d = data as any;
  const sess = d?.session;
  const structured =
    sess?.turns ?? sess?.messages ?? d?.turns ?? d?.messages ?? d?.lines;
  if (Array.isArray(structured) && structured.length > 0) {
    return normalizeConversationTranscript(structured);
  }
  const blob =
    typeof sess?.transcript === 'string' ? sess.transcript : typeof d?.transcript === 'string' ? d.transcript : '';
  if (blob.trim()) return parseTranscriptStringBlob(blob);
  return [];
}

export async function fetchConversationTranscript(sessionId: string, axiosInstance?: AxiosInstance): Promise<ConversationLine[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.analytics}/conversation/${sessionId}`;
  const response = await client.get<unknown>(url);
  return normalizeConversationTranscript(response.data);
}
